import React, { useEffect, useState } from "react";
import {
    Button,
    FormControl,
    Glyphicon,
    Label,
    Table,
} from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Link } from "react-router-dom";
import {
    createHardwareUnit,
    getAllBuilders,
    getCapsAdminOnly,
    getUnits,
} from "../utils/bedrockAPI";
import { getUser } from "../utils/Common";
import {
    STATUS_OPTIONS,
    apiErrorMessage,
    formatPortSsh,
    formatTimezoneDisplay,
    productType,
    softwareStatusStyle,
    softwareUpdates,
    statusStyle,
    capShortId,
    capSiteLabel,
    sticker,
    unitLifecycle,
    yesNo,
} from "../utils/unitDisplay";
import "./Admin.css";
import UnitExpandPanel from "./UnitExpandPanel";
import "./Units.css";

const LIST_COLUMNS = 14;

export default function Units({ history }) {
    const [units, setUnits] = useState([]);
    const [capsById, setCapsById] = useState({});
    const [buildersById, setBuildersById] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("INVENTORY");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [error, setError] = useState(null);

    const loadData = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            const [unitRows, activeCaps, builders] = await Promise.all([
                getUnits(),
                getCapsAdminOnly(["BUILD", "ACTIVE"]).catch(() => []),
                getAllBuilders().catch(() => []),
            ]);
            const capIndex = {};
            (activeCaps || []).forEach((cap) => {
                if (cap?.capId) capIndex[cap.capId] = cap;
            });
            const builderIndex = {};
            (Array.isArray(builders) ? builders : []).forEach((builder) => {
                if (builder?.builderId) builderIndex[builder.builderId] = builder;
            });
            const nextUnits = Array.isArray(unitRows) ? unitRows : [];
            setUnits(nextUnits);
            setCapsById(capIndex);
            setBuildersById(builderIndex);
            return { units: nextUnits, capsById: capIndex };
        } catch (err) {
            setError(apiErrorMessage(err));
            setUnits([]);
            return { units: [], capsById: {} };
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        if (getUser().role !== "ADMIN") {
            history.push("/dashboard");
            return;
        }
        loadData();
    }, []);

    const handleCreate = async (kind) => {
        setCreating(kind);
        setError(null);
        try {
            const cap = await createHardwareUnit(kind);
            await loadData({ silent: true });
            if (cap?.unitId) {
                setExpandedId(cap.unitId);
                setStatusFilter("INVENTORY");
            }
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setCreating(null);
    };

    const followLifecycle = (nextUnit, nextCap) => {
        if (!nextUnit) return;
        const nextLife = unitLifecycle(nextUnit, nextCap);
        setStatusFilter((prev) => (prev && nextLife !== prev ? nextLife : prev));
    };

    const handleUnitPatched = (updated) => {
        if (!updated?.unitId) return;
        setUnits((prev) =>
            prev.map((row) => {
                if (row.unitId !== updated.unitId) return row;
                return {
                    ...row,
                    ...updated,
                    latestNote: Object.prototype.hasOwnProperty.call(updated, "latestNote")
                        ? updated.latestNote
                        : row.latestNote,
                    replacedByUnitId: Object.prototype.hasOwnProperty.call(updated, "replacedByUnitId")
                        ? updated.replacedByUnitId
                        : row.replacedByUnitId,
                };
            })
        );
        if (updated.currentCapId && updated.port != null) {
            setCapsById((prev) => {
                const cap = prev[updated.currentCapId];
                if (!cap) return prev;
                return {...prev, [updated.currentCapId]: {...cap, port: updated.port}};
            });
        }
        const currentCap = updated.currentCapId ? capsById[updated.currentCapId] : null;
        followLifecycle(updated, currentCap);
    };

    const handleCapsChanged = async (replacedUnit) => {
        const keepId = replacedUnit?.unitId || expandedId;
        const { units: nextUnits, capsById: nextCaps } = await loadData({ silent: true });
        if (!keepId) return;
        setExpandedId(keepId);
        const row = nextUnits.find((unit) => unit.unitId === keepId);
        const cap = row?.currentCapId ? nextCaps[row.currentCapId] : null;
        followLifecycle(row, cap);
    };

    const handleSearchChange = (e) => {
        const next = e.target.value;
        const startedSearch = !searchTerm.trim() && next.trim();
        setSearchTerm(next);
        if (startedSearch) {
            setStatusFilter("");
        }
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        setStatusFilter("INVENTORY");
    };

    const term = searchTerm.trim().toLowerCase();
    const filtered = units.filter((unit) => {
        const currentCap = unit.currentCapId ? capsById[unit.currentCapId] : null;
        const lifecycle = unitLifecycle(unit, currentCap);
        if (statusFilter && lifecycle !== statusFilter) return false;
        if (!term) return true;
        const updates = softwareUpdates(unit, currentCap);
        const builder = currentCap?.builderId ? buildersById[currentCap.builderId] : null;
        const hay = [
            unit.unitId,
            sticker(unit.unitId),
            lifecycle,
            unit.architecture,
            productType(unit.architecture),
            unit.port != null ? String(unit.port) : "",
            unit.port != null ? String(unit.port - 5780) : "",
            unit.currentCapId,
            currentCap?.name,
            currentCap?.neighborhood,
            currentCap?.status,
            builder?.name,
            updates.hostname,
            updates.timezone,
            updates.yolo,
            unit.latestNote,
            unit.replacesUnitId,
            unit.replacedByUnitId,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return hay.includes(term);
    });

    return (
        <div className="units-page admin-page">
            <header className="admin-header">
                <nav className="brand-breadcrumb">
                    <Link to="/admin">Admin</Link>
                    <span className="brand-breadcrumb-sep">/</span>
                    <span className="brand-breadcrumb-current">JobSight units</span>
                </nav>
                <h1 className="admin-title">JobSight units</h1>
                {/* <p className="units-subtitle">
                    Inventory is BUILD units with no builder. Assigning a builder queues SHIPPED; Mark arrived when it gets to the jobsite; the customer activating it makes it DEPLOYED. Expand a row for notes and properties. Returned boxes: New rental (keep sticker) — do not Add JobSight.{" "}
                    <Link to="/admin/sims">SIM inventory</Link>
                </p> */}
            </header>

            <div className="units-add-buttons">
                <Button
                    bsStyle="warning"
                    bsSize="small"
                    disabled={!!creating || loading}
                    onClick={() => handleCreate("JobSight")}
                >
                    <Glyphicon glyph="plus" /> {creating === "JobSight" ? "Adding…" : "Add JobSight"}
                </Button>
                <Button
                    bsStyle="info"
                    bsSize="small"
                    disabled={!!creating || loading}
                    onClick={() => handleCreate("PRO")}
                >
                    <Glyphicon glyph="plus" /> {creating === "PRO" ? "Adding…" : "Add PRO"}
                </Button>
                <Button
                    bsStyle="success"
                    bsSize="small"
                    disabled={!!creating || loading}
                    onClick={() => handleCreate("Legacy")}
                >
                    <Glyphicon glyph="plus" /> {creating === "Legacy" ? "Adding…" : "Add Legacy"}
                </Button>
            </div>

            <div className="units-toolbar">
                <div className="units-search-wrap">
                    <FormControl
                        type="text"
                        placeholder="Search sticker, builder, hostname, port…"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="units-search-input"
                    />
                    {searchTerm ? (
                        <Button
                            bsStyle="link"
                            bsSize="xsmall"
                            className="units-search-clear"
                            onClick={handleClearSearch}
                            title="Clear search"
                        >
                            <Glyphicon glyph="remove" />
                        </Button>
                    ) : null}
                </div>
                <FormControl
                    componentClass="select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="units-status-filter"
                >
                    <option value="">All statuses</option>
                    {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                            {status}
                        </option>
                    ))}
                </FormControl>
                <Button bsStyle="default" onClick={() => loadData({ silent: units.length > 0 })} disabled={loading && units.length === 0}>
                    <Glyphicon glyph="refresh" />
                </Button>
            </div>

            {error && <div className="units-error">{error}</div>}
            {loading && units.length === 0 && <div className="units-loading">Loading units…</div>}
            {(!loading || units.length > 0) && (
                <>
                    <div className="units-count">
                        {filtered.length} unit{filtered.length === 1 ? "" : "s"}
                        {statusFilter ? ` · ${statusFilter}` : ""}
                    </div>
                    {filtered.length === 0 ? (
                        <div className="units-empty">No units match.</div>
                    ) : (
                        <div className="units-table-wrapper">
                            <Table striped condensed className="units-table">
                                <thead>
                                    <tr>
                                        <th className="units-copy-cell" />
                                        <th />
                                        <th>Sticker</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th title="Tunnel port / SSH port (port − 5780)">Port / SSH</th>
                                        <th>Software</th>
                                        <th>YOLO</th>
                                        <th>MP4</th>
                                        <th>24/7</th>
                                        <th>Hostname</th>
                                        <th>TZ</th>
                                        <th>Current cap</th>
                                        <th>Latest note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((unit) => {
                                        const currentCap = unit.currentCapId
                                            ? capsById[unit.currentCapId]
                                            : null;
                                        const lifecycle = unitLifecycle(unit, currentCap);
                                        const updates = softwareUpdates(unit, currentCap);
                                        const tzDisp = formatTimezoneDisplay(updates.timezone);
                                        const capLabel = unit.currentCapId
                                            ? capShortId(currentCap, unit.currentCapId)
                                            : "—";
                                        const siteLabel = capSiteLabel(currentCap);
                                        const builder = currentCap?.builderId
                                            ? buildersById[currentCap.builderId]
                                            : null;
                                        const expanded = expandedId === unit.unitId;
                                        return (
                                            <React.Fragment key={unit.unitId}>
                                                <tr
                                                    className={expanded ? "units-row-open" : "units-row"}
                                                    onClick={() =>
                                                        setExpandedId(expanded ? null : unit.unitId)
                                                    }
                                                >
                                                    <td
                                                        className="units-copy-cell"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <CopyToClipboard text={unit.unitId}>
                                                            <Button
                                                                bsSize="xsmall"
                                                                bsStyle="default"
                                                                title="Copy unit ID"
                                                            >
                                                                <Glyphicon glyph="duplicate" />
                                                            </Button>
                                                        </CopyToClipboard>
                                                    </td>
                                                    <td className="units-expand-caret">
                                                        <Glyphicon glyph={expanded ? "chevron-down" : "chevron-right"} />
                                                    </td>
                                                    <td>
                                                        <span className="units-sticker">{sticker(unit.unitId)}</span>
                                                    </td>
                                                    <td>{productType(unit.architecture || currentCap?.architecture)}</td>
                                                    <td>
                                                        <Label bsStyle={statusStyle(lifecycle)}>{lifecycle}</Label>
                                                    </td>
                                                    <td>{formatPortSsh(unit.port ?? currentCap?.port)}</td>
                                                    <td>
                                                        {updates.status ? (
                                                            <Label bsStyle={softwareStatusStyle(updates.status)}>
                                                                {updates.status === "UPDATE_AVAILABLE"
                                                                    ? "AVAILABLE"
                                                                    : updates.status}
                                                            </Label>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td>{updates.yolo != null ? updates.yolo : "—"}</td>
                                                    <td>{yesNo(updates.mediamtx)}</td>
                                                    <td>{yesNo(updates.footage)}</td>
                                                    <td>{updates.hostname || "—"}</td>
                                                    <td>{tzDisp || "—"}</td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        {unit.currentCapId ? (
                                                            <>
                                                                <Link to={`/caps/${unit.currentCapId}`}>{capLabel}</Link>
                                                                {siteLabel && (
                                                                    <div className="units-id-muted">{siteLabel}</div>
                                                                )}
                                                                {builder?.name && (
                                                                    <div className="units-id-muted">{builder.name}</div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>
                                                    <td
                                                        className="units-latest-note"
                                                        title={unit.latestNote || ""}
                                                    >
                                                        {unit.latestNote || "—"}
                                                    </td>
                                                </tr>
                                                {expanded && (
                                                    <tr className="units-expand-row">
                                                        <td colSpan={LIST_COLUMNS} onClick={(e) => e.stopPropagation()}>
                                                            <UnitExpandPanel
                                                                unit={unit}
                                                                cap={currentCap}
                                                                builderName={builder?.name}
                                                                onUnitPatched={handleUnitPatched}
                                                                onCapsChanged={handleCapsChanged}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
